import { MealPreferences } from "@/clients/models/MealPreferences";
import { MealTime } from "@/clients/models/MealTime";
import SaveButton from "@/components/button/save-button";
import { PageContainer } from "@/components/container/page-container";
import { PageTitleComponent } from "@/components/page-title";
import { MealTimeSegmentButtons } from "@/components/segment-buttons/meal-time-segment-buttons";
import { mealTabs } from "@/constants/meal-tab";
import { QueryKeys } from "@/constants/query-keys";
import { useGetMealPreferences } from "@/hooks/requests/meal-preference/use-get-meal-preferences";
import { useUpdateMealPreferences } from "@/hooks/requests/meal-preference/use-meal-preferences";
import { useToast } from "@/hooks/ui/use-toast";
import { setProfileContext } from "@/utils/sentry/tools";
import {
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonReorder,
  IonReorderGroup,
  ItemReorderEventDetail,
  useIonRouter,
} from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AddFoodSearchBar from "./add-food-seach-bar";

export default function MyFoodProfile() {
  const location = useLocation();
  const navigation = useIonRouter();
  const searchParams = new URLSearchParams(location.search);
  const mealTime =
    (searchParams.get("mealTime") as MealTime) ?? MealTime.BREAKFAST;

  const [foods, setFoods] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const { mealPreferences } = useGetMealPreferences(mealTime);
  const { updateMealPreferences, isUpdating } =
    useUpdateMealPreferences(mealTime);
  const { showError, showInfo } = useToast();
  // const [weekendMode, setWeekendMode] = useState(false);

  useEffect(() => {
    if (mealPreferences) {
      console.log("mealPreferences", mealPreferences);
      const uniqueFoods = new Set(mealPreferences.weekdayChoices);
      setFoods(Array.from(uniqueFoods));
    }
  }, [mealPreferences]);

  function handleReorder(event: CustomEvent<ItemReorderEventDetail>) {
    console.log("Dragged from index", event.detail.from, "to", event.detail.to);
    event.detail.complete();
    setFoods((prev) => {
      const newFoods = [...prev];
      newFoods[event.detail.from] = newFoods[event.detail.to];
      newFoods[event.detail.to] = prev[event.detail.from];
      return newFoods;
    });
  }

  const onAddFood = (food: string) => {
    if (foods.includes(food)) {
      showInfo("Food already added");
      return;
    }
    setFoods((prev) => [...prev, food]);
  };

  const onDeleteFood = (food: string) => {
    setFoods((prev) => prev.filter((f) => f !== food));
  };

  const onSave = () => {
    console.log(foods);
    if (mealTime === MealTime.SNACK) {
      if (foods.length < 3) {
        showInfo("Please select at least 3 snacks to continue.");
        return;
      }
    } else {
      if (foods.length === 0) {
        showInfo("Please add at least one food");
        return;
      }
    }
    const index = mealTabs.indexOf(mealTime);
    const nextMealTime = mealTabs[index + 1];
    const uniqueFoods = new Set(foods);
    const mealPreferences: MealPreferences = {
      weekdayChoices: Array.from(uniqueFoods),
      weekEndsChoices: Array.from(uniqueFoods),
    };
    updateMealPreferences(mealPreferences, {
      onSuccess: () => {
        setFoods([]);
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.MEAL_PREFERENCES, mealTime],
        });
        setProfileContext(
          `${QueryKeys.MEAL_PREFERENCES}-${mealTime}`,
          mealPreferences,
        );
        if (nextMealTime) {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set("mealTime", nextMealTime);
          navigation.push(
            `${location.pathname}?${newSearchParams.toString()}`,
            "none",
          );
        } else {
          navigation.push("/profiling/creating", "none");
        }
      },
      onError: (error) => {
        showError(error.message);
      },
    });
  };

  return (
    <PageContainer>
      <PageTitleComponent
        title="My Food Profile"
        description="Where your food choices and your lifestyle come together."
      />

      <MealTimeSegmentButtons
        selected={mealTime}
        // onSelect={(mealTime) => {
        //   const newSearchParams = new URLSearchParams(searchParams);
        //   newSearchParams.set("mealTime", mealTime);
        //   navigation.push(`${location.pathname}?${newSearchParams.toString()}`, "none");
        // }}
      />

      <div className="w-full">
        <AddFoodSearchBar
          onAddFood={onAddFood}
          selectedFoodSet={new Set(foods)}
          mealTime={mealTime}
        />
      </div>

      <IonReorderGroup
        className="w-full h-full border-t border-gray-100 overflow-auto"
        disabled={false}
        onIonItemReorder={handleReorder}
      >
        {foods.map((food, index) => (
          <IonItemSliding key={food}>
            <IonItem lines="none" className="px-2 border-b border-gray-100">
              <IonLabel className="text-sm! font-medium! py-4">
                <span className="mr-4!">{index + 1}</span>
                <span>{food}</span>
              </IonLabel>
              <IonReorder slot="end" />
            </IonItem>

            <IonItemOptions side="end">
              <IonItemOption color="danger" onClick={() => onDeleteFood(food)}>
                Delete
              </IonItemOption>
            </IonItemOptions>
          </IonItemSliding>
        ))}
      </IonReorderGroup>

      {mealTime === MealTime.SNACK && (
        <span className="text-sm text-gray-500">
          Please select at least 3 snacks to continue.
        </span>
      )}
      <div className="w-full py-4">
        <SaveButton
          text="Save & Continue"
          position="static"
          disabled={isUpdating}
          onSave={onSave}
        />
      </div>
    </PageContainer>
  );
}
